import { AuthService } from "../services/AuthService.js";
import { AuthViewModel } from "./AuthViewModel.js";
import { SESSION_EXPIRED_MESSAGE, getLoginErrorMessage, getNewPasswordErrorMessage, getPasswordResetErrorMessage, getPickUserErrorMessage, isSessionExpiredError } from "../utils/auth-error-messages.js";
import { ViewModel, singleton } from "mvc-kit";
//#region src/modules/AuthWrapper2/viewmodels/LoginViewModel.ts
var LoginView = /* @__PURE__ */ function(LoginView) {
	LoginView["LegacyUser"] = "LEGACY_USER";
	LoginView["LoginForm"] = "LOGIN_FORM";
	LoginView["NewPassword"] = "NEW_PASSWORD";
	LoginView["NewPasswordSuccess"] = "NEW_PASSWORD_SUCCESS";
	LoginView["PasswordReset"] = "PASSWORD_RESET";
	LoginView["PickUser"] = "PICK_USER";
	LoginView["ResetRequested"] = "RESET_REQUESTED";
	return LoginView;
}({});
var LoginViewModel = class extends ViewModel {
	static DEFAULT_STATE = {
		activeView: "LOGIN_FORM",
		authToken: "",
		error: "",
		loginPassToken: "",
		resetEmail: "",
		resetRequested: false,
		resetToken: "",
		users: []
	};
	authService = singleton(AuthService);
	onInit() {
		const urlParams = new URLSearchParams(window.location.search);
		const email = urlParams.get("resetEmail");
		const password = urlParams.get("tempPass");
		if (email && password) {
			this.stripAutoLoginParamsFromUrl();
			this.handleLogin(email, password);
		}
	}
	/**
	* Every action here resolves rather than rejects. The error is mapped to
	* user-facing copy and stored on `state.error`, which is the single source the
	* views render — see AuthWrapper2.md. Letting these reject would leave an
	* unhandled rejection at every call site, since none of them await.
	*/
	async handleLogin(email, password) {
		this.set({ error: "" });
		let data;
		try {
			data = await this.authService.authenticate(email, password);
		} catch (e) {
			this.set({ error: getLoginErrorMessage(e) });
			return;
		}
		if (data.legacyUser) {
			this.set({ activeView: "LEGACY_USER" });
			return;
		}
		if (!data.users?.length) {
			this.set({ error: "Incorrect email or password. Please try again." });
			return;
		}
		this.set({
			activeView: "PICK_USER",
			loginPassToken: data.loginPassToken,
			users: data.users
		});
		if (data.users.length === 1) await this.handleLoginAsUser(data.users[0].user.id);
	}
	async handleLoginAsUser(userId) {
		if (!this.state.loginPassToken) {
			this.backToLoginForm(SESSION_EXPIRED_MESSAGE);
			return;
		}
		this.set({ error: "" });
		try {
			const data = await this.authService.authenticateAsUser(this.state.loginPassToken, userId);
			if (data.requireResetPassword) this.set({
				activeView: "NEW_PASSWORD",
				authToken: data.authToken,
				resetToken: data.passwordChangeToken || ""
			});
			else singleton(AuthViewModel).setAuthToken(data.authToken);
		} catch (e) {
			const message = getPickUserErrorMessage(e);
			if (isSessionExpiredError(e)) this.backToLoginForm(message);
			else this.set({ error: message });
		}
	}
	async requestPasswordReset() {
		this.set({ error: "" });
		try {
			await this.authService.requestResetPassword(this.state.resetEmail);
		} catch (e) {
			const message = getPasswordResetErrorMessage(e);
			if (message) {
				this.set({ error: message });
				return;
			}
		}
		this.set({
			activeView: "RESET_REQUESTED",
			resetRequested: true
		});
	}
	async setNewPassword(password) {
		this.set({ error: "" });
		try {
			await this.authService.setNewPassword(password, this.state.resetToken);
		} catch (e) {
			this.set({ error: getNewPasswordErrorMessage(e) });
			return;
		}
		this.set({ activeView: "NEW_PASSWORD_SUCCESS" });
	}
	completeNewPasswordFlow() {
		if (this.state.authToken) singleton(AuthViewModel).setAuthToken(this.state.authToken);
	}
	/**
	* Returns to a clean login form, optionally with an error to explain why.
	*
	* Deliberately not `reset()`: that clears mvc-kit's async tracking and re-runs
	* onInit(), which would wipe the message we are trying to show and re-fire the
	* URL auto-login.
	*/
	backToLoginForm(error = "") {
		this.set({
			activeView: "LOGIN_FORM",
			authToken: "",
			error,
			loginPassToken: "",
			resetToken: "",
			users: []
		});
	}
	showPasswordReset() {
		this.set({
			activeView: "PASSWORD_RESET",
			error: ""
		});
	}
	showLoginForm() {
		this.set({
			activeView: "LOGIN_FORM",
			error: ""
		});
	}
	showLoginFormWithResetNote() {
		this.set({
			activeView: "LOGIN_FORM",
			error: "",
			resetRequested: true
		});
	}
	setResetEmail(resetEmail) {
		this.set({ resetEmail });
	}
	stripAutoLoginParamsFromUrl() {
		const url = new URL(window.location.href);
		url.searchParams.delete("resetEmail");
		url.searchParams.delete("tempPass");
		window.history.replaceState({}, "", url.toString());
	}
};
//#endregion
export { LoginView, LoginViewModel };

//# sourceMappingURL=LoginViewModel.js.map